import {
  Alert,
  Button,
  Checkbox,
  Flex,
  InputNumber,
  Modal,
  Radio,
  Space,
  Spin,
} from 'antd';

import { useEditProductGroupSalePriceModal } from '@app/components/Modals/EditProductGroupSalePriceModal/hooks';
import { InfoRow } from '@app/components/Modals/EditProductGroupSalePriceModal/ui/InfoRow';
import { formatAmount } from '@app/shared/utils/formatAmount';

import type { FC } from 'react';

import '@app/components/Modals/EditProductGroupSalePriceModal/styles/EditProductGroupSalePriceModal.desktop.scss';

export interface EditProductGroupSalePriceModalProps {
  open: boolean;
  productId: number;
  warehouseId: number;
  productName?: string | null;
  warehouseName?: string | null;
  currentMinSalePrice?: number | null;
  currentMaxSalePrice?: number | null;
  onClose(): void;
  afterSave?(): void;
  /** When true, hides the price section and opens directly in stock control mode. */
  forceLeftoverControl?: boolean;
}

const PF = 'EditProductGroupSalePriceModal';

export const EditProductGroupSalePriceModal: FC<
  EditProductGroupSalePriceModalProps
> = (props) => {
  const {
    open,
    productName,
    warehouseName,
    currentMinSalePrice,
    currentMaxSalePrice,
    onClose,
    forceLeftoverControl = false,
  } = props;
  const {
    isSetSalePrice,
    setIsSetSalePrice,
    priceChangeType,
    setPriceChangeType,
    percentDirection,
    setPercentDirection,
    priceValue,
    setPriceValue,

    isSetLeftoverControl,
    setIsSetLeftoverControl,
    leftoverMinimal,
    setLeftoverMinimal,
    isNotifyAtMinimal,
    setIsNotifyAtMinimal,
    leftoverCritical,
    setLeftoverCritical,
    isNotifyAtCritical,
    setIsNotifyAtCritical,

    isSaving,
    isLoadingLeftoverControl,
    isLeftoverControlLoadFailed,
    canSubmit,
    saveHandler,
  } = useEditProductGroupSalePriceModal(props);

  const currentPriceLabel =
    currentMinSalePrice != null && currentMaxSalePrice != null
      ? currentMinSalePrice === currentMaxSalePrice
        ? formatAmount(currentMinSalePrice)
        : `${formatAmount(currentMinSalePrice, {
            showCurrency: false,
          })} — ${formatAmount(currentMaxSalePrice)}`
      : '—';
  const isFormDisabled =
    isSaving || isLoadingLeftoverControl || isLeftoverControlLoadFailed;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        forceLeftoverControl
          ? 'Контроль остатков'
          : 'Изменение цены продажи и контроля остатков'
      }
      destroyOnHidden
      className={PF}
      width={560}
      footer={
        <Flex
          className={`${PF}__footer`}
          justify="space-between"
          align="center"
        >
          <Button onClick={onClose} disabled={isSaving}>
            Отменить
          </Button>

          <Button
            type="primary"
            onClick={saveHandler}
            loading={isSaving}
            disabled={!canSubmit}
          >
            Сохранить
          </Button>
        </Flex>
      }
    >
      <Spin spinning={isLoadingLeftoverControl}>
        <div className={`${PF}__content`}>
          {isLeftoverControlLoadFailed && (
            <Alert
              type="error"
              showIcon
              message="Не удалось загрузить текущие настройки контроля остатков"
            />
          )}

          <div className={`${PF}__rows`}>
            <InfoRow label="Товар:" value={productName ?? '—'} />
            <InfoRow label="Склад:" value={warehouseName ?? '—'} />
            <InfoRow label="Текущая цена продажи:" value={currentPriceLabel} />
          </div>

          {!forceLeftoverControl && (
            <div className={`${PF}__section`}>
              <Checkbox
                checked={isSetSalePrice}
                onChange={(e) => setIsSetSalePrice(e.target.checked)}
                disabled={isFormDisabled}
              >
                <span className={`${PF}__sectionTitle`}>
                  Изменить цену продажи
                </span>
              </Checkbox>

              {isSetSalePrice && (
                <div className={`${PF}__sectionBody`}>
                  <Radio.Group
                    value={priceChangeType}
                    onChange={(e) => setPriceChangeType(e.target.value)}
                    disabled={isFormDisabled}
                  >
                    <Space direction="vertical">
                      <Radio value="fixed">Фиксированная цена</Radio>
                      <Radio value="percent">Изменить на процент</Radio>
                    </Space>
                  </Radio.Group>

                  <Flex
                    className={`${PF}__priceRow`}
                    align="center"
                    gap={12}
                    wrap="wrap"
                  >
                    {priceChangeType === 'percent' && (
                      <Radio.Group
                        value={percentDirection}
                        onChange={(e) => setPercentDirection(e.target.value)}
                        disabled={isFormDisabled}
                      >
                        <Radio.Button value="increase">Увеличить</Radio.Button>
                        <Radio.Button value="decrease">Уменьшить</Radio.Button>
                      </Radio.Group>
                    )}

                    <InputNumber
                      className={`${PF}__priceInput`}
                      min={0}
                      max={priceChangeType === 'percent' ? 100 : undefined}
                      step={1}
                      precision={2}
                      value={priceValue}
                      onChange={setPriceValue}
                      addonAfter={priceChangeType === 'percent' ? '%' : ''}
                      disabled={isFormDisabled}
                      controls
                    />
                  </Flex>
                </div>
              )}
            </div>
          )}

          <div className={`${PF}__section`}>
            {!forceLeftoverControl && (
              <Checkbox
                checked={isSetLeftoverControl}
                onChange={(e) => setIsSetLeftoverControl(e.target.checked)}
                disabled={isFormDisabled}
              >
                <span className={`${PF}__sectionTitle`}>Контроль остатков</span>
              </Checkbox>
            )}

            {(forceLeftoverControl || isSetLeftoverControl) && (
              <div className={`${PF}__sectionBody`}>
                <Flex className={`${PF}__leftoverRow`} align="center" gap={12}>
                  <span className={`${PF}__leftoverLabel`}>
                    Минимальный остаток:
                  </span>
                  <InputNumber
                    className={`${PF}__leftoverInput`}
                    min={0}
                    step={1}
                    precision={0}
                    value={leftoverMinimal}
                    onChange={setLeftoverMinimal}
                    disabled={isFormDisabled}
                    controls
                  />
                </Flex>

                <Checkbox
                  checked={isNotifyAtMinimal}
                  onChange={(e) => setIsNotifyAtMinimal(e.target.checked)}
                  disabled={isFormDisabled}
                >
                  Оповещать владельцев о минимальном остатке
                </Checkbox>

                <Flex className={`${PF}__leftoverRow`} align="center" gap={12}>
                  <span className={`${PF}__leftoverLabel`}>
                    Критический остаток:
                  </span>
                  <InputNumber
                    className={`${PF}__leftoverInput`}
                    min={0}
                    step={1}
                    precision={0}
                    value={leftoverCritical}
                    onChange={setLeftoverCritical}
                    disabled={isFormDisabled}
                    controls
                  />
                </Flex>

                <Checkbox
                  checked={isNotifyAtCritical}
                  onChange={(e) => setIsNotifyAtCritical(e.target.checked)}
                  disabled={isFormDisabled}
                >
                  Оповещать владельцев о критическом остатке
                </Checkbox>
              </div>
            )}
          </div>
        </div>
      </Spin>
    </Modal>
  );
};
